if (!customElements.get('product-load-more')) {
  class ProductLoadMore extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.onClick = this.onClick.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.mobileMedia = window.matchMedia('(max-width: 749px)');

      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.initialize();
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stopLoadingFeedback();
      this.button?.removeEventListener('click', this.onClick);
      this.mobileMedia?.removeEventListener('change', this.onBreakpointChange);
    }

    initialize() {
      const collection = this.closest('.collection');
      this.grid = collection?.querySelector(this.dataset.gridSelector);
      this.button = this.querySelector('[data-load-more-button]');
      this.label = this.querySelector('[data-load-more-label]');
      this.status = this.querySelector('[data-load-more-status]');
      this.nextUrl = this.dataset.nextUrl || '';
      this.defaultLabel = this.dataset.loadMoreLabel || '더 보기';
      this.loadingLabel = this.dataset.loadingLabel || '상품을 불러오는 중...';
      this.loadingMessages = window.FwdLoadingFeedback?.messages || [this.loadingLabel];
      this.loadedLabel = this.dataset.loadedLabel || '[count]개 상품을 추가했습니다.';
      this.errorLabel = this.dataset.errorLabel || '상품을 불러오지 못했습니다. 다시 시도해 주세요.';

      if (!this.grid || !this.button) return;

      this.button.addEventListener('click', this.onClick);
      this.mobileMedia.addEventListener('change', this.onBreakpointChange);
      this.prepareInitialBatch();
    }

    get items() {
      if (!this.grid) return [];
      return Array.from(this.grid.children).filter((item) => item.matches(this.dataset.itemSelector));
    }

    get batchSize() {
      const value = this.mobileMedia.matches
        ? this.dataset.mobileBatchSize
        : this.dataset.desktopBatchSize;
      return Number.parseInt(value, 10) || (this.mobileMedia.matches ? 24 : 48);
    }

    get hiddenItems() {
      return this.items.filter((item) => item.hidden);
    }

    prepareInitialBatch() {
      if (this.mobileMedia.matches) {
        this.items.slice(this.batchSize).forEach((item) => {
          item.hidden = true;
        });
      } else {
        this.revealAllItems();
      }
      this.updateControl();
    }

    onBreakpointChange(event) {
      if (!event.matches) this.revealAllItems();
      this.updateControl();
    }

    revealAllItems() {
      this.items.forEach((item) => {
        item.hidden = false;
      });
    }

    revealLocalBatch() {
      const batch = this.hiddenItems.slice(0, this.batchSize);
      batch.forEach((item) => {
        item.hidden = false;
      });
      return batch;
    }

    async onClick(event) {
      event.preventDefault();
      if (this.loading) return;

      const revealed = this.revealLocalBatch();
      if (revealed.length) {
        this.announce(revealed.length);
        this.updateControl();
        return;
      }

      if (!this.nextUrl) return;
      await this.loadNextPage();
    }

    async loadNextPage() {
      this.setLoading(true);

      try {
        const requestUrl = new URL(this.nextUrl, window.location.origin);
        if (requestUrl.pathname === window.routes?.search_url && window.FwdSearchQuery) {
          FwdSearchQuery.applyStorefrontContract(requestUrl.searchParams, FwdSearchQuery.readDisplayQuery());
        }
        requestUrl.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(requestUrl, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Product page request failed: ${response.status}`);

        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const sourceController = parsed.querySelector('product-load-more');
        const sourceCollection = sourceController?.closest('.collection');
        const sourceGrid = sourceCollection?.querySelector(sourceController?.dataset.gridSelector || '');
        if (!sourceController || !sourceGrid) throw new Error('Product page response is missing load-more markup');

        const incomingItems = Array.from(sourceGrid.children).filter((item) =>
          item.matches(sourceController.dataset.itemSelector)
        );
        if (!incomingItems.length) throw new Error('Product page response contains no products');

        incomingItems.forEach((item) => {
          item.classList.add('scroll-trigger--cancel');
          item.hidden = this.mobileMedia.matches;
          this.grid.appendChild(item);
        });
        this.nextUrl = sourceController.dataset.nextUrl || '';
        this.dataset.nextUrl = this.nextUrl;

        document.dispatchEvent(
          new CustomEvent('collection:product-grid:rendered', {
            detail: {
              containerId: 'ProductGridContainer',
              appended: true,
            },
          })
        );

        const revealed = this.mobileMedia.matches ? this.revealLocalBatch() : incomingItems;
        this.announce(revealed.length);
        this.updateControl();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.status.textContent = this.errorLabel;
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.toggleAttribute('aria-busy', isLoading);
      if (isLoading || this.button.hidden) {
        this.button.setAttribute('aria-disabled', 'true');
      } else {
        this.button.removeAttribute('aria-disabled');
      }
      this.button.classList.toggle('loading', isLoading);
      if (isLoading) {
        this.startLoadingFeedback();
      } else {
        this.stopLoadingFeedback();
        this.label.textContent = this.defaultLabel;
      }
    }

    startLoadingFeedback() {
      this.stopLoadingFeedback();
      const messages = this.loadingMessages?.length ? this.loadingMessages : [this.loadingLabel];
      this.loadingMessageIndex = 0;
      this.label.textContent = messages[0] || this.loadingLabel;
      if (typeof window.setInterval !== 'function') return;
      this.loadingFeedbackTimer = window.setInterval(() => {
        this.loadingMessageIndex = (this.loadingMessageIndex + 1) % messages.length;
        this.label.textContent = messages[this.loadingMessageIndex];
      }, 1600);
    }

    stopLoadingFeedback() {
      if (this.loadingFeedbackTimer && typeof window.clearInterval === 'function') {
        window.clearInterval(this.loadingFeedbackTimer);
      }
      this.loadingFeedbackTimer = null;
    }

    announce(count) {
      this.status.textContent = this.loadedLabel.replace('[count]', String(count));
    }

    updateControl() {
      const hasMore = this.hiddenItems.length > 0 || Boolean(this.nextUrl);
      this.button.hidden = !hasMore;
      this.toggleAttribute('data-complete', !hasMore);
      if (this.nextUrl) {
        this.button.href = this.nextUrl;
      } else if (!hasMore) {
        this.button.removeAttribute('href');
      }
      if (hasMore && !this.loading) this.button.removeAttribute('aria-disabled');
      if (!hasMore) this.button.setAttribute('aria-disabled', 'true');
    }
  }

  customElements.define('product-load-more', ProductLoadMore);
}
